'use client';

import React, { useState, useEffect } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

// ============================================
// useIsMobile Hook (Internal)
// ============================================
const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [breakpoint]);

  return isMobile;
};

// ============================================
// ListRow.Image
// ============================================
interface ListRowImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  defaultThumb?: string;
  responsive?: boolean;
}

const imageVariants = cva('object-cover flex-shrink-0', {
  variants: {
    size: {
      sm: 'w-8 h-8',
      md: 'w-12 h-12',
      lg: 'w-16 h-16',
      xl: 'w-20 h-20',
    },
    rounded: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    size: 'md',
    rounded: 'md',
  },
});

// 반응형 이미지 사이즈 (모바일에서 한 단계 작게)
const responsiveImageVariants = cva('object-cover flex-shrink-0', {
  variants: {
    size: {
      sm: 'w-6 h-6 md:w-8 md:h-8',
      md: 'w-10 h-10 md:w-12 md:h-12',
      lg: 'w-12 h-12 md:w-16 md:h-16',
      xl: 'w-16 h-16 md:w-20 md:h-20',
    },
    rounded: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    size: 'md',
    rounded: 'md',
  },
});

const ListRowImage = React.forwardRef<HTMLImageElement, ListRowImageProps>(
  (
    {
      className,
      size,
      rounded,
      src,
      alt = '',
      style,
      defaultThumb,
      responsive = true,
      ...props
    },
    ref,
  ) => {
    const variants = responsive ? responsiveImageVariants : imageVariants;

    return (
      <img
        ref={ref}
        src={src}
        alt={alt}
        className={cn(variants({ size, rounded }), className)}
        style={style}
        onError={e => {
          if (defaultThumb) {
            (e.target as HTMLImageElement).src = defaultThumb;
          }
        }}
        {...props}
      />
    );
  },
);

ListRowImage.displayName = 'ListRow.Image';

// ============================================
// TruncateWithTooltip (Internal Component)
// ============================================
const DEFAULT_TRUNCATE_LENGTH = 30;
const MOBILE_TRUNCATE_LENGTH = 18;

type TruncateWithTooltipProps = {
  content: React.ReactNode;
  truncateLength?: number;
  mobileTruncateLength?: number;
  responsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

const TruncateWithTooltip: React.FC<TruncateWithTooltipProps> = ({
  content,
  truncateLength = DEFAULT_TRUNCATE_LENGTH,
  mobileTruncateLength = MOBILE_TRUNCATE_LENGTH,
  responsive = true,
  className,
  style,
}) => {
  const isMobile = useIsMobile();
  const effectiveTruncateLength =
    responsive && isMobile ? mobileTruncateLength : truncateLength;

  const textContent = typeof content === 'string' ? content : null;
  const isTruncated =
    textContent && textContent.length > effectiveTruncateLength;

  // 문자열이 아니거나 truncate 필요 없으면 그대로 표시
  if (!isTruncated) {
    return (
      <span className={className} style={style}>
        {content}
      </span>
    );
  }

  // truncate된 텍스트
  const truncatedText =
    textContent.substring(0, effectiveTruncateLength) + '...';

  return (
    <span
      className={cn(
        'relative group/tooltip inline-block max-w-full',
        className,
      )}
    >
      <span className="block" style={style}>
        {truncatedText}
      </span>
      <span
        className={cn(
          'absolute left-0 bottom-full mb-1 z-50',
          'px-2 py-1.5 rounded-md shadow-lg',
          'bg-gscale-900 text-white text-xs',
          'whitespace-normal break-words',
          'opacity-0 invisible',
          'group-hover/tooltip:opacity-100 group-hover/tooltip:visible',
          'transition-opacity duration-200',
          'pointer-events-none',
        )}
        style={{ minWidth: '120px', maxWidth: '280px' }}
      >
        {textContent}
        <span className="absolute left-3 top-full border-4 border-transparent border-t-gscale-900" />
      </span>
    </span>
  );
};

// ============================================
// ListRow.Texts
// ============================================
type TextType = '1Row' | '2RowTypeA' | '2RowTypeB' | '3Row';

interface TextStyleProps extends React.CSSProperties {
  color?: string;
  fontWeight?: string | number;
  fontSize?: string | number;
}

type ListRowTextsProps = {
  type?: TextType;
  top?: React.ReactNode;
  topProps?: TextStyleProps;
  topCl?: string;
  middle?: React.ReactNode;
  middleProps?: TextStyleProps;
  middleCl?: string;
  bottom?: React.ReactNode;
  bottomProps?: TextStyleProps;
  bottomCl?: string;
  className?: string;
  truncateLength?: number;
  mobileTruncateLength?: number;
  showTooltip?: boolean;
  responsive?: boolean;
};

const textsVariants = cva('flex flex-col justify-center w-fit', {
  variants: {
    type: {
      '1Row': 'gap-0',
      '2RowTypeA': 'gap-0.5',
      '2RowTypeB': 'gap-1',
      '3Row': 'gap-0.5',
    },
  },
  defaultVariants: {
    type: '2RowTypeA',
  },
});

const ListRowTexts: React.FC<ListRowTextsProps> = ({
  type = '2RowTypeA',
  top,
  topProps,
  topCl,
  middle,
  middleProps,
  middleCl,
  bottom,
  bottomProps,
  bottomCl,
  className,
  truncateLength = DEFAULT_TRUNCATE_LENGTH,
  mobileTruncateLength = MOBILE_TRUNCATE_LENGTH,
  showTooltip = true,
  responsive = true,
}) => {
  const renderText = (
    content: React.ReactNode,
    styleProps?: TextStyleProps,
    defaultClassName?: string,
    customClassName?: string,
  ) => {
    if (!content) return null;

    if (showTooltip) {
      return (
        <TruncateWithTooltip
          content={content}
          truncateLength={truncateLength}
          mobileTruncateLength={mobileTruncateLength}
          responsive={responsive}
          className={cn(defaultClassName, customClassName)}
          style={styleProps}
        />
      );
    }

    return (
      <span
        className={cn('truncate', defaultClassName, customClassName)}
        style={styleProps}
      >
        {content}
      </span>
    );
  };

  return (
    <div className={cn(textsVariants({ type }), className)}>
      {renderText(top, topProps, 'text-sm font-medium text-gscale-950', topCl)}
      {type === '3Row' &&
        renderText(
          middle,
          middleProps,
          'text-xs font-normal text-gscale-700',
          middleCl,
        )}
      {renderText(
        bottom,
        bottomProps,
        'text-xs font-normal text-gscale-500',
        bottomCl,
      )}
    </div>
  );
};

ListRowTexts.displayName = 'ListRow.Texts';

// ============================================
// ListRow.Icon
// ============================================
type ListRowIconProps = {
  icon: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

const ListRowIcon: React.FC<ListRowIconProps> = ({
  icon,
  className,
  style,
}) => {
  return (
    <div
      className={cn(
        'flex items-center justify-center flex-shrink-0',
        className,
      )}
      style={style}
    >
      {icon}
    </div>
  );
};

ListRowIcon.displayName = 'ListRow.Icon';

// ============================================
// ListRow.Button (Polymorphic Component)
// ============================================
const buttonVariants = cva(
  'inline-flex items-center justify-center font-medium transition-colors duration-200 flex-shrink-0',
  {
    variants: {
      variant: {
        normal:
          'bg-gscale-100 text-gscale-800 hover:bg-gscale-200 hover:text-gscale-950',
        primary: 'bg-gtred text-white hover:bg-gtred/90',
        secondary: 'bg-gscale-100 text-gscale-700 hover:bg-gscale-200',
        outline: 'border border-gscale-300 text-gscale-700 hover:bg-gscale-50',
        ghost: 'text-gscale-600 hover:bg-gscale-100 hover:text-gscale-900',
        danger: 'bg-red-500 text-white hover:bg-red-600',
        link: 'text-gtred underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-6 px-2 text-xs rounded',
        sm: 'h-8 px-3 text-xs rounded-md',
        md: 'h-9 px-4 text-sm rounded-md',
        lg: 'h-10 px-5 text-sm rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'normal',
      size: 'sm',
    },
  },
);

interface ListRowButtonProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof buttonVariants> {
  as?: React.ElementType | React.ComponentType<any>;
  loading?: boolean;
  newTab?: boolean;
  disabled?: boolean;
  href?: string;
  target?: string;
  rel?: string;
  type?: 'button' | 'submit' | 'reset';
  [key: string]: any; // Allow any additional props for polymorphic components
}

const ListRowButton = React.forwardRef<HTMLElement, ListRowButtonProps>(
  (
    {
      as,
      className,
      variant,
      size,
      disabled,
      loading,
      children,
      onClick,
      newTab,
      href,
      target,
      rel,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const Component = as || (href ? 'a' : 'button');

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled || loading) {
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      onClick?.(e);
    };

    const isLink = Component === 'a' || href;
    const linkTarget = newTab ? '_blank' : target;
    const linkRel = newTab ? 'noopener noreferrer' : rel;

    const componentProps: any = {
      ref,
      className: cn(
        buttonVariants({ variant, size }),
        disabled && 'opacity-50 cursor-not-allowed',
        loading && 'opacity-70 cursor-wait',
        isLink && disabled && 'pointer-events-none',
        className,
      ),
      onClick: handleClick,
      ...props,
    };

    if (isLink) {
      componentProps.href = href;
      componentProps.target = linkTarget;
      componentProps.rel = linkRel;
      componentProps['aria-disabled'] = disabled;
    } else {
      componentProps.disabled = disabled || loading;
      componentProps.type = type;
    }

    return (
      <Component {...componentProps}>
        {loading ? (
          <span className="mr-2 inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : null}
        {children}
      </Component>
    );
  },
);

ListRowButton.displayName = 'ListRow.Button';

// ============================================
// ListRow.Buttons (Button Group)
// ============================================
type ListRowButtonsProps = {
  children: React.ReactNode;
  gap?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
};

const buttonsGapVariants = cva('flex items-center flex-shrink-0', {
  variants: {
    gap: {
      xs: 'gap-1',
      sm: 'gap-2',
      md: 'gap-3',
      lg: 'gap-4',
    },
  },
  defaultVariants: {
    gap: 'sm',
  },
});

const ListRowButtons: React.FC<ListRowButtonsProps> = ({
  children,
  gap = 'sm',
  className,
}) => {
  return (
    <div className={cn(buttonsGapVariants({ gap }), className)}>{children}</div>
  );
};

ListRowButtons.displayName = 'ListRow.Buttons';

// ============================================
// ListRow (Main Component)
// ============================================
const listRowVariants = cva('flex items-center w-full', {
  variants: {
    variant: {
      default: 'bg-white rounded-lg',
      ghost: 'bg-transparent',
      card: 'bg-white rounded-lg shadow-sm',
    },
    size: {
      sm: 'py-2 px-3',
      md: 'py-3 px-4',
      lg: 'py-4 px-5',
      none: 'p-0',
    },
    hoverable: {
      true: 'hover:bg-gscale-50 transition-colors duration-200 cursor-pointer',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    hoverable: false,
  },
});

interface ListRowProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'content'>,
    VariantProps<typeof listRowVariants> {
  as?: React.ElementType | React.ComponentType<any>;
  left?: React.ReactNode;
  contents?: React.ReactNode;
  right?: React.ReactNode;
  divider?: boolean;
  href?: string;
  target?: string;
  rel?: string;
  newTab?: boolean;
  [key: string]: any;
}

const ListRowRoot = React.forwardRef<HTMLElement, ListRowProps>(
  (
    {
      as,
      className,
      variant,
      size,
      hoverable,
      left,
      contents,
      right,
      divider = false,
      onClick,
      href,
      target,
      rel,
      newTab = false,
      ...props
    },
    ref,
  ) => {
    const isLink = !!href;
    const isClickable = !!onClick || isLink;
    const Component = as || (href ? 'a' : 'div');

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      onClick?.(e);
    };

    const linkTarget = newTab ? '_blank' : target;
    const linkRel = newTab ? 'noopener noreferrer' : rel;

    const componentProps: any = {
      className: cn(
        listRowVariants({
          variant,
          size,
          hoverable: hoverable ?? isClickable,
        }),
        className,
      ),
      onClick: handleClick,
      role: isClickable && !isLink ? 'button' : undefined,
      tabIndex: isClickable && !isLink ? 0 : undefined,
      ...props,
    };

    if (isLink) {
      componentProps.href = href;
      componentProps.target = linkTarget;
      componentProps.rel = linkRel;
    }

    return (
      <div ref={ref as any} className="w-full">
        <Component {...componentProps}>
          {left && <div className="flex-shrink-0">{left}</div>}
          {contents && (
            <div className={`flex-1 min-w-0 ${left ? 'ml-4' : ''}`}>
              {contents}
            </div>
          )}
          {right && <div className="flex-shrink-0">{right}</div>}
        </Component>
        {divider && (
          <div className="flex justify-center w-full">
            <div className="w-[98%] h-px bg-gscale-50" />
          </div>
        )}
      </div>
    );
  },
);

ListRowRoot.displayName = 'ListRow';

// ============================================
// Compound Component Export
// ============================================
export const ListRow = Object.assign(ListRowRoot, {
  Image: ListRowImage,
  Texts: ListRowTexts,
  Icon: ListRowIcon,
  Button: ListRowButton,
  Buttons: ListRowButtons,
});

export type {
  ListRowProps,
  ListRowImageProps,
  ListRowTextsProps,
  ListRowIconProps,
  ListRowButtonProps,
  ListRowButtonsProps,
};
